import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

const vehicleData = [
  {
    id: 1,
    name: 'BMW M2 G87',
    engine: '3.0L Biturbo R6 · S58B30',
    originalPS: 460,
    stage1PS: 540,
    originalNm: 550,
    stage1Nm: 650,
    image: '/images/vehicles/bmw-m2-g87-460ps.webp',
  },
  {
    id: 2,
    name: 'Mercedes G63 AMG',
    engine: '4.0L Biturbo V8 · M177',
    originalPS: 585,
    stage1PS: 700,
    originalNm: 850,
    stage1Nm: 1000,
    image: '/images/vehicles/mercedes-g63-amg-585ps.webp',
  },
  {
    id: 3,
    name: 'Golf 7 GTI',
    engine: '2.0L TSI · EA888 Gen3',
    originalPS: 245,
    stage1PS: 310,
    originalNm: 370,
    stage1Nm: 440,
    image: '/images/vehicles/golf-7-gti-245ps.webp',
  },
  {
    id: 4,
    name: 'Audi RS5 B9',
    engine: '2.9L TFSI V6 Biturbo',
    originalPS: 450,
    stage1PS: 520,
    originalNm: 600,
    stage1Nm: 700,
    image: '/images/vehicles/audi-rs5-b9-450ps.webp',
  },
  {
    id: 5,
    name: 'Toyota Supra MK5',
    engine: '3.0L Turbo R6 · B58',
    originalPS: 340,
    stage1PS: 420,
    originalNm: 500,
    stage1Nm: 600,
    image: '/images/vehicles/toyota-supra-mk5-340ps.webp',
  },
  {
    id: 6,
    name: 'BMW M140i F20',
    engine: '3.0L Turbo R6 · B58B30',
    originalPS: 340,
    stage1PS: 400,
    originalNm: 500,
    stage1Nm: 580,
    image: '/images/vehicles/bmw-m140i-f20-340ps.webp',
  },
  {
    id: 7,
    name: 'Aston Martin DB11',
    engine: '4.0L Biturbo V8 · M177',
    originalPS: 510,
    stage1PS: 600,
    originalNm: 675,
    stage1Nm: 780,
    image: '/images/vehicles/aston-martin-db11-510ps.webp',
  },
  {
    id: 8,
    name: 'Audi A4 B9 45 TFSI',
    engine: '2.0L TFSI · EA888 Gen3',
    originalPS: 265,
    stage1PS: 330,
    originalNm: 370,
    stage1Nm: 440,
    image: '/images/vehicles/audi-a4-b9-45tfsi-265ps.webp',
  },
  {
    id: 9,
    name: 'Skoda Octavia RS 5E',
    engine: '2.0L TSI · EA888',
    originalPS: 245,
    stage1PS: 310,
    originalNm: 370,
    stage1Nm: 440,
    image: '/images/vehicles/skoda-octavia-rs-5e-245ps.webp',
  },
  {
    id: 10,
    name: 'Skoda Kodiaq RS',
    engine: '2.0L TDI Biturbo · EA288',
    originalPS: 245,
    stage1PS: 300,
    originalNm: 500,
    stage1Nm: 580,
    image: '/images/vehicles/skoda-kodiaq-245ps.webp',
  },
];

const VehicleCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        goToNext();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [currentIndex, isAnimating]);

  const goToPrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + vehicleData.length) % vehicleData.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % vehicleData.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToSlide = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const currentVehicle = vehicleData[currentIndex];
  const prevIndex = (currentIndex - 1 + vehicleData.length) % vehicleData.length;
  const nextIndex = (currentIndex + 1) % vehicleData.length;
  const gainPS = currentVehicle.stage1PS - currentVehicle.originalPS;
  const gainNm = currentVehicle.stage1Nm - currentVehicle.originalNm;

  return (
    <section className="py-24 bg-[#0D0D0D] relative overflow-hidden" data-testid="vehicle-carousel">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#EC2229]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-3">
            Echte <span className="text-[#EC2229]">Ergebnisse</span>
          </h2>
          <p className="text-slate-500 text-lg">Messbar auf dem Prüfstand</p>
        </div>

        {/* Carousel */}
        <div className="relative flex items-center justify-center h-[340px] mb-8">
          {/* Left Preview */}
          <div 
            className="absolute left-[5%] w-[200px] h-[180px] cursor-pointer transition-all duration-400 z-5 transform -translate-x-[10%] hidden lg:block"
            onClick={goToPrev}
          >
            <img 
              src={vehicleData[prevIndex].image} 
              alt={vehicleData[prevIndex].name}
              className="w-full h-full object-contain opacity-35 grayscale-[30%] hover:opacity-60 hover:grayscale-0 transition-all duration-400"
            />
          </div>

          {/* Main Car */}
          <div className="relative w-[520px] h-[320px] flex items-center justify-center z-10">
            <img
              src={currentVehicle.image}
              alt={currentVehicle.name}
              className={`max-w-full max-h-full object-contain transition-all duration-500 drop-shadow-[0_30px_60px_rgba(0,0,0,0.7)] ${isAnimating ? 'scale-90 opacity-50' : ''}`}
            />
            
            {/* Power Badge */}
            <div className="absolute top-2 right-5 bg-[#EC2229] px-4 py-2 rounded-lg flex flex-col items-center shadow-[0_8px_24px_rgba(236,34,41,0.4)]">
              <span className="text-2xl font-extrabold text-white leading-none">+{gainPS}</span>
              <span className="text-xs font-semibold text-white/80 uppercase">PS</span>
            </div>
          </div>

          {/* Right Preview */}
          <div 
            className="absolute right-[5%] w-[200px] h-[180px] cursor-pointer transition-all duration-400 z-5 transform translate-x-[10%] hidden lg:block"
            onClick={goToNext}
          >
            <img 
              src={vehicleData[nextIndex].image} 
              alt={vehicleData[nextIndex].name}
              className="w-full h-full object-contain opacity-35 grayscale-[30%] hover:opacity-60 hover:grayscale-0 transition-all duration-400"
            />
          </div>

          {/* Navigation Arrows */}
          <button 
            onClick={goToPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border-2 border-white/15 bg-[#141414]/90 text-white/70 flex items-center justify-center transition-all hover:border-[#EC2229] hover:bg-[#EC2229] hover:text-white hover:scale-110 z-20 backdrop-blur-sm"
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>
          <button 
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border-2 border-white/15 bg-[#141414]/90 text-white/70 flex items-center justify-center transition-all hover:border-[#EC2229] hover:bg-[#EC2229] hover:text-white hover:scale-110 z-20 backdrop-blur-sm"
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Info Card */}
        <div className="bg-[#1E1E23]/95 border border-white/8 rounded-2xl p-6 md:p-8 max-w-4xl mx-auto backdrop-blur-xl mb-6">
          <div className="text-center mb-6 pb-4 border-b border-white/8">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-1">{currentVehicle.name}</h3>
            <p className="text-sm text-white/45 font-mono">{currentVehicle.engine}</p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            {/* Original */}
            <div className="text-center min-w-[140px]">
              <span className="block text-[10px] text-white/40 uppercase tracking-wider mb-1">Originalleistung</span>
              <span className="text-2xl md:text-3xl font-bold text-white/50">
                {currentVehicle.originalPS} <small className="text-sm font-medium opacity-60">PS</small>
              </span>
            </div>

            <ArrowRight className="text-white/20 rotate-90 md:rotate-0" size={20} />

            {/* Stage 1 */}
            <div className="text-center min-w-[140px]">
              <span className="block text-[10px] text-white/40 uppercase tracking-wider mb-1">TrXL Stage 1</span>
              <span className="text-2xl md:text-3xl font-bold text-[#EC2229]">
                {currentVehicle.stage1PS} <small className="text-sm font-medium opacity-60">PS</small>
              </span>
            </div>

            {/* Gains */}
            <div className="flex gap-3">
              <div className="bg-emerald-500/15 border border-emerald-500/30 px-4 py-2 rounded-lg text-center">
                <span className="text-sm font-bold text-emerald-500">+{gainPS} PS</span>
              </div>
              <div className="bg-emerald-500/15 border border-emerald-500/30 px-4 py-2 rounded-lg text-center">
                <span className="text-sm font-bold text-emerald-500">+{gainNm} Nm</span>
              </div>
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2">
          {vehicleData.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2.5 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-[#EC2229] w-8' 
                  : 'bg-white/20 w-2.5 hover:bg-white/40'
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default VehicleCarousel;
